import { h, render } from 'preact';
import { cdnBaseFromSrc, configUrl } from './cdn';
import {
  DEFAULT_PRESENTATION,
  parseLiveConfig,
  type LiveConfig,
  type LiveQuestion,
} from './config';
import { resolveConsent, type TcfWindow } from './consent';
import { hostAllowed } from './domains';
import {
  behaviourFor,
  canLeave,
  chooseOption,
  freeTextFields,
  hasAnswer,
  isCorrect,
  isScorable,
  pickOutcome,
  revealsAnswer,
  scoreFor,
  showsFinalResult,
  type Answer,
  type SubmissionField,
} from './engine';
import {
  answerEvent,
  buildContext,
  completeEvent,
  startEvent,
  submitEvent,
  type AnswerShape,
  type CompleteData,
  type EventBase,
} from './event';
import { readVote, rememberVote, resolveIdentity } from './identity';
import { computeShares, parseResults } from './results';
import { buildStyles, reserveFor, RESERVE_ATTRIBUTE, SIGNUP_RESERVE_PX } from './styles';
import { postSubmission, submissionsUrl } from './submissions';
import {
  consentTextOf,
  looksLikeEmail,
  postSubscription,
  subscriptionsUrl,
  type SubscriptionStatus,
} from './subscriptions';
import { createSender, getJson, type FetchLike } from './transport';
import { View, type SignupView, type ViewActions, type ViewState } from './ui';
import { warnOnce } from './warn';

/**
 * Mounting, and the state machine every format runs on.
 *
 * The machine is the same for all five: ask the questions in order, commit an answer when the
 * reader leaves a question, and finish. What differs is read from the behaviour table in engine.ts,
 * so a poll and a survey take the same path through this file.
 *
 * Nothing in here is allowed to throw into the host page. Every entry point and every async path
 * is wrapped, and a failure hides the embed rather than leaving a broken box in the middle of an
 * article.
 *
 * Mounting sends nothing. The first event a reader can produce is `start`, on the first answer they
 * commit. Views are counted from the CloudFront access logs for the config request instead, which
 * ARCHITECTURE D22 explains.
 */
export interface MountDeps {
  readonly win: Window;
  readonly cdnBase: string;
  readonly fetchImpl?: FetchLike | undefined;
  readonly consentTimeoutMs?: number | undefined;
}

const INTERACTION_ID = /^int_[0-9a-f]{32}$/;
const MOUNTED_ATTR = 'data-quizee-mounted';

const NEEDS_ANSWER = 'This question needs an answer.';
const SUBMIT_FAILED = 'We could not save your answers. Please try again.';
const NEEDS_EMAIL = 'Enter an email address so we know where to send it.';
const SIGNUP_FAILED = 'We could not sign you up just then. Please try again.';

/** Where a publisher says on the page this embed sits. See `placementRef` for what may be in it. */
const REF_ATTRIBUTE = 'data-quizee-ref';

/** Guards against a second mount within this bundle instance. The attribute guards across two. */
const mountedElements = new WeakSet<Element>();

export function mountAll(deps: MountDeps): void {
  try {
    const nodes = deps.win.document.querySelectorAll('[data-quizee]');
    for (const node of Array.from(nodes)) mountOne(node, deps);
  } catch (error) {
    warnOnce('could not scan the page for embeds', error);
  }
}

export function mountOne(host: Element, deps: MountDeps): void {
  try {
    if (mountedElements.has(host) || host.hasAttribute(MOUNTED_ATTR)) return;

    const interactionId = host.getAttribute('data-quizee') ?? '';
    if (!INTERACTION_ID.test(interactionId)) {
      warnOnce('ignoring an embed whose data-quizee is not an interaction id');
      return;
    }
    if (host.shadowRoot !== null) return;

    mountedElements.add(host);
    host.setAttribute(MOUNTED_ATTR, '');
    void runWidget(host, interactionId, deps);
  } catch (error) {
    warnOnce('could not mount an embed', error);
  }
}

/**
 * The answer shape for a question that can be recorded the moment the reader leaves it.
 *
 * Free text returns null: its value has to reach the submissions store first, because the event
 * can only carry the `value_text_ref` that comes back from there. Those answers are sent at the
 * end of the interaction instead, by `sendTextAnswers` below. That is the whole reason the cross
 * field rule in the event schema exists.
 */
function answerShape(question: LiveQuestion, answer: Answer): AnswerShape | null {
  if (answer.kind === 'choice') return { selected_option_ids: [...answer.option_ids] };
  if (answer.kind === 'number' && question.type === 'scale') return { value_number: answer.value };
  return null;
}

async function runWidget(host: Element, interactionId: string, deps: MountDeps): Promise<void> {
  const doc = deps.win.document;
  const shadow = host.attachShadow({ mode: 'open' });

  /**
   * How much space to hold, decided before anything is fetched.
   *
   * A published snippet carries the exact height for this interaction at this width, so a new
   * embed reserves what it is about to need and the article never moves. Older snippets carry
   * nothing, and those fall back to a default read from what interactions of that width actually
   * render at. Either way the number is settled here, on the first line, rather than after a round
   * trip to the CDN.
   *
   * The width is the widget's own box, not the viewport's: an embed in a sidebar is narrow on a
   * wide screen, and narrow is what makes it tall.
   */
  const width = host.getBoundingClientRect().width;
  let reserved = reserveFor(width, host.getAttribute(RESERVE_ATTRIBUTE));

  const styleEl = doc.createElement('style');
  styleEl.textContent = buildStyles(DEFAULT_PRESENTATION, reserved);
  const mountPoint = doc.createElement('div');
  shadow.appendChild(styleEl);
  shadow.appendChild(mountPoint);

  let actions: ViewActions = {
    choose: () => undefined,
    setText: () => undefined,
    setNumber: () => undefined,
    next: () => undefined,
    back: () => undefined,
    retry: () => undefined,
    setEmail: () => undefined,
    toggleConsent: () => undefined,
    subscribe: () => undefined,
  };

  /** Filled in once the config has been read, and null on every document without a signup. */
  let signupView: () => SignupView | null = () => null;

  /**
   * A question that is taller than the one before it would push the rest of the article down, so
   * the box never shrinks: whatever the tallest state needed is held for the rest of the visit.
   */
  const hold = (): void => {
    try {
      const box = mountPoint.firstElementChild as HTMLElement | null;
      const height = box?.offsetHeight ?? 0;
      if (height > reserved) {
        reserved = height;
        mountPoint.style.minHeight = `${reserved}px`;
      }
    } catch {
      // Measuring is an optimisation. A browser that will not measure still renders.
    }
  };

  /** What is on screen, so the signup can redraw it without the caller having to say what it was. */
  let current: ViewState = { kind: 'loading' };

  const draw = (next: ViewState, moveFocus = false): void => {
    current = next;
    render(h(View, { state: next, actions, signup: signupView() }), mountPoint);
    hold();
    if (!moveFocus) return;
    try {
      const target = mountPoint.querySelector('[data-qz-focus]') as HTMLElement | null;
      target?.focus();
    } catch {
      // Focus is a courtesy, never a requirement.
    }
  };

  const hide = (): void => {
    render(null, mountPoint);
    styleEl.textContent = ':host{display:none}';
  };

  try {
    draw({ kind: 'loading' });

    const fetchImpl = deps.fetchImpl ?? deps.win.fetch;
    if (typeof fetchImpl !== 'function') {
      hide();
      warnOnce('this browser has no fetch, so the embed cannot load');
      return;
    }
    const doFetch: FetchLike = (input, init) => fetchImpl.call(deps.win, input, init);

    const [rawConfig, consent] = await Promise.all([
      getJson(configUrl(deps.cdnBase, interactionId), doFetch),
      resolveConsent(deps.win as unknown as TcfWindow, deps.consentTimeoutMs),
    ]);

    const config = parseLiveConfig(rawConfig);
    if (config === null || config.interaction_id !== interactionId) {
      hide();
      warnOnce(`no usable published config for ${interactionId}`);
      return;
    }

    /**
     * Is this page one the interaction is published for.
     *
     * A copied snippet renders today, because the config and the results are static on a CDN with a
     * permissive CORS header and only writes are origin checked. It looks like it works, records
     * nothing, and counts as a view against the publisher it was stolen from. The published document
     * now says where it belongs, and this is the widget honouring it.
     *
     * Refusing is silent on purpose. This is somebody else's article, and a box with an error in it
     * is worse than no box: the one line in the console is for the developer who put the snippet
     * there and is wondering why nothing appeared.
     *
     * `hide()` is the same path a broken document takes, which is what settles the reserved space:
     * the reserve lives in the shadow stylesheet, and hiding replaces that whole stylesheet, so the
     * held height goes with it and the page closes the gap rather than keeping a blank band forever.
     */
    if (!hostAllowed(deps.win.location?.hostname ?? '', config.domains)) {
      hide();
      warnOnce(
        `not rendering ${interactionId} here: it is published for ${(config.domains ?? []).join(', ')}`,
      );
      return;
    }

    const first = config.questions[0];
    if (first === undefined) {
      hide();
      return;
    }

    /**
     * The signup, and the space it will need.
     *
     * The step is on the completion screen, which arrives long after the article was laid out
     * around this embed, so the room for it is taken now rather than when it appears. Growing the
     * box at the end would push the rest of the article down at the moment the reader finished.
     *
     * No endpoint means no step: an address the widget cannot deliver anywhere is not worth asking
     * a reader for, and a button that could only ever fail is worse than no button.
     */
    const subscribeUrl =
      config.endpoints.subscriptions ?? subscriptionsUrl(config.endpoints.events);
    const signup = subscribeUrl === null ? undefined : config.signup;
    if (signup !== undefined) reserved += SIGNUP_RESERVE_PX;

    styleEl.textContent = buildStyles(config.presentation, reserved);

    const behaviour = behaviourFor(config.format);
    const reveals = revealsAnswer(config);
    const identity = resolveIdentity(consent.storage_allowed);
    const base: EventBase = {
      interaction_id: interactionId,
      interaction_version: config.version,
      identity,
      consent,
      context: buildContext(
        deps.win,
        host.getAttribute('data-quizee-article-id'),
        host.getAttribute(REF_ATTRIBUTE),
      ),
    };

    const sender = createSender(config.endpoints.events, doFetch);
    const shownAt = Date.now();

    // Everything the reader has done. Mutable on purpose: the view is rebuilt from it on every
    // draw, so there is one place to look for what state the interaction is in.
    const answers: Record<string, Answer | undefined> = {};
    const revealed = new Set<number>();
    const committed = new Set<number>();
    // How long the reader spent on each free text question, kept because the event for it cannot
    // be sent until the end, by which time the timer has moved on several questions.
    const textTiming = new Map<string, number>();
    /**
     * The signup's own state, kept apart from the interaction's on purpose.
     *
     * Subscribing is a separate act with its own consent, so it has its own busy flag and its own
     * error: a reader whose subscription failed has still completed the interaction, and a reader
     * who never touches the step has still completed it too.
     */
    let email = '';
    let ticked = false;
    let signupBusy = false;
    let signupError: string | null = null;
    let signupDone: SubscriptionStatus | null = null;

    signupView = (): SignupView | null =>
      signup === undefined
        ? null
        : {
            signup,
            email,
            ticked,
            busy: signupBusy,
            error: signupError,
            done: signupDone,
          };

    let index = 0;
    let started = false;
    let busy = false;
    let error: string | null = null;
    let questionShownAt = shownAt;
    let finished = false;

    const drawQuestion = (moveFocus = false): void => {
      const question = config.questions[index];
      if (question === undefined) return;
      draw(
        {
          kind: 'question',
          config,
          index,
          answer: answers[question.id],
          revealed: revealed.has(index),
          busy,
          error,
        },
        moveFocus,
      );
    };

    const showAggregate = async (chosen: string | null, includeOwnVote: boolean): Promise<void> => {
      const endpoint = config.endpoints.results;
      if (endpoint === undefined) {
        draw({ kind: 'thanks', config }, true);
        return;
      }
      const results = parseResults(await getJson(endpoint, doFetch));
      if (results === null) {
        // Results are a projection and can lag or fail. A thank you is honest; an error is not
        // something the reader can do anything about.
        draw({ kind: 'thanks', config }, true);
        return;
      }
      const tally = computeShares(first.options, results, chosen, includeOwnVote);
      draw({ kind: 'results', config, chosen, total: tally.total, shares: tally.shares }, true);
    };

    /**
     * Record leaving a question.
     *
     * One answer event per question per commit, and re-answering after going back clears the mark
     * so the change is recorded too. Events are facts, so a changed answer is a second fact rather
     * than an edit of the first.
     */
    const commit = (at: number): void => {
      if (committed.has(at)) return;
      const question = config.questions[at];
      if (question === undefined) return;
      const answer = answers[question.id];
      if (!hasAnswer(answer) || answer === undefined) return;

      committed.add(at);
      // Remembered before anything is sent, so a reader whose network dropped is still not asked
      // the same poll question again on their next visit.
      if (behaviour.remember && answer.kind === 'choice' && answer.option_ids[0] !== undefined) {
        rememberVote(interactionId, answer.option_ids[0], consent.storage_allowed);
      }
      if (!started) {
        started = true;
        sender.send(startEvent(base));
      }

      const shape = answerShape(question, answer);
      if (shape === null) {
        textTiming.set(question.id, Math.max(0, Date.now() - questionShownAt));
        return;
      }

      const scored = behaviour.score && isScorable(question);
      sender.send(
        answerEvent(base, {
          question_id: question.id,
          question_index: at,
          response_ms: Math.max(0, Date.now() - questionShownAt),
          ...(scored ? { client_marked_correct: isCorrect(question, answer) } : {}),
          ...shape,
        }),
      );
    };

    /**
     * One answer event per free text question, once the store has given each value a reference.
     *
     * This is what makes a survey's funnel readable: without it a form would have nothing between
     * `start` and `submit`, so nobody could see which question readers give up on. A question the
     * store did not hand back a reference for gets no event, because the alternative is inventing
     * an id for a record that may not exist.
     *
     * Only one event per question even if the reader went back and retyped, because only the value
     * that was actually stored has a reference to point at.
     */
    const sendTextAnswers = (
      fields: readonly SubmissionField[],
      refs: ReadonlyMap<string, string>,
    ): void => {
      const indexOf = new Map(config.questions.map((question, at) => [question.id, at]));
      for (const field of fields) {
        const ref = refs.get(field.question_id);
        const at = indexOf.get(field.question_id);
        // Driven by what was actually posted, not by what came back, so a store that answered with
        // a reference for a question the reader left blank cannot conjure an answer out of it.
        if (ref === undefined || at === undefined) continue;
        const elapsed = textTiming.get(field.question_id);
        sender.send(
          answerEvent(base, {
            question_id: field.question_id,
            question_index: at,
            ...(elapsed === undefined ? {} : { response_ms: elapsed }),
            value_text_ref: ref,
          }),
        );
      }
    };

    /**
     * The end of the interaction. `redraw` is false on a retry, where the reader is looking at the
     * failure message rather than at the last question and should not be flicked back to it.
     */
    const finish = async (redraw = true): Promise<void> => {
      busy = true;
      error = null;
      if (redraw) drawQuestion();

      try {
        const data: CompleteData = { duration_ms: Math.max(0, Date.now() - shownAt) };
        const tally = behaviour.score ? scoreFor(config, answers) : null;
        const outcome = behaviour.outcome ? pickOutcome(config, answers) : null;

        const fields = behaviour.submission ? freeTextFields(config, answers) : [];
        // A survey of nothing but choice questions has no values to store, so there is nothing for
        // a submit event to reference and no reason to make the reader depend on that endpoint.
        if (fields.length > 0) {
          // The document carries the url. The derivation is only for documents published before
          // `endpoints.submissions` existed, which are still live and still have to work.
          const url = config.endpoints.submissions ?? submissionsUrl(config.endpoints.events);
          const receipt =
            url === null
              ? null
              : await postSubmission(url, doFetch, {
                  interaction_id: config.interaction_id,
                  interaction_version: config.version,
                  session_id: identity.session_id,
                  fields,
                });
          if (receipt === null) {
            // Nothing is lost: the answers are still held, the queued events are still queued, and
            // the reader is told plainly rather than being shown a receipt that does not exist.
            busy = false;
            warnOnce('could not save a submission');
            draw({ kind: 'failed', config, message: SUBMIT_FAILED }, true);
            return;
          }
          // The values are stored, so the references now exist. Answers first, then the submit
          // that ties them together, in the order the stream reads best.
          sendTextAnswers(fields, receipt.refs);
          sender.send(
            submitEvent(base, {
              submission_ref: receipt.submission_ref,
              field_count: fields.length,
            }),
          );
        }

        sender.send(
          completeEvent(base, {
            ...data,
            ...(tally === null ? {} : tally),
            ...(outcome === null ? {} : { outcome_id: outcome.id }),
          }),
        );
        finished = true;
        busy = false;
        await sender.flush();

        if (!showsFinalResult(config)) {
          draw({ kind: 'thanks', config }, true);
          return;
        }
        if (behaviour.aggregate) {
          const chosen = answers[first.id];
          const optionId =
            chosen !== undefined && chosen.kind === 'choice'
              ? (chosen.option_ids[0] ?? null)
              : null;
          await showAggregate(optionId, true);
          return;
        }
        if (tally !== null) {
          draw({ kind: 'score', config, score: tally.score, max: tally.max_score }, true);
          return;
        }
        if (outcome !== null) {
          draw({ kind: 'outcome', config, outcome }, true);
          return;
        }
        draw({ kind: 'thanks', config }, true);
      } catch (err) {
        busy = false;
        warnOnce('could not record the response', err);
        draw({ kind: 'thanks', config }, true);
      }
    };

    const advance = (): void => {
      commit(index);
      if (index >= config.questions.length - 1) {
        void finish();
        return;
      }
      index += 1;
      error = null;
      questionShownAt = Date.now();
      // Send what has been answered so far. A reader who abandons a ten question survey halfway
      // has still told us something, and keepalive covers the ones who leave mid question.
      void sender.flush();
      drawQuestion(true);
    };

    /** Every handler is a door into the host page's event loop, so every one of them is wrapped. */
    const guard =
      <T = void>(fn: (value: T) => void) =>
      (value: T): void => {
        try {
          if (busy || finished) return;
          fn(value);
        } catch (err) {
          warnOnce('could not handle an interaction', err);
        }
      };

    /** Redraw whatever screen the reader is on, which for the signup is always a finished one. */
    const redraw = (): void => draw(current);

    /**
     * The signup's handlers are guarded on their own state rather than on the interaction's.
     *
     * `guard` refuses once `finished` is true, which is right for answering a question and wrong
     * here: the step only exists after the interaction is over. Consent is not consulted either,
     * because a reader who refused storage can still subscribe. That is a separate act, with its
     * own consent given for its own purpose, and nothing about it is written to the device.
     */
    const signupGuard =
      <T = void>(fn: (value: T) => void) =>
      (value: T): void => {
        try {
          if (signupBusy) return;
          fn(value);
        } catch (err) {
          warnOnce('could not handle the signup', err);
        }
      };

    /**
     * Send the address, then say what actually happened to it.
     *
     * The wording is driven by the endpoint's answer, not by the publisher's `double_opt_in`
     * setting, so a reader is never told they are subscribed while a confirmation email is still
     * unopened. The setting is only the fallback for a response that said nothing readable.
     *
     * `consent_text` is the exact wording this reader was shown, sent so the record says what they
     * agreed to rather than what the brand's settings say by the time anybody asks.
     *
     * A failure keeps everything: the address is still in the field and the tick is still ticked,
     * so trying again is one press rather than a retyped address.
     */
    const subscribe = async (url: string, wording: string): Promise<void> => {
      signupBusy = true;
      signupError = null;
      redraw();

      try {
        const result = await postSubscription(url, doFetch, {
          interaction_id: config.interaction_id,
          interaction_version: config.version,
          session_id: identity.session_id,
          email: email.trim(),
          consent_text: wording,
          ...(base.context.placement_ref === undefined
            ? {}
            : { placement_ref: base.context.placement_ref }),
        });

        signupBusy = false;
        if (result === null) {
          signupError = SIGNUP_FAILED;
          warnOnce('could not sign a reader up');
          redraw();
          return;
        }
        signupDone =
          result === 'accepted'
            ? (signup?.double_opt_in ?? true)
              ? 'pending'
              : 'subscribed'
            : result;
        redraw();
      } catch (err) {
        signupBusy = false;
        signupError = SIGNUP_FAILED;
        warnOnce('could not sign a reader up', err);
        redraw();
      }
    };

    actions = {
      choose: guard((optionId: string): void => {
        const question = config.questions[index];
        if (question === undefined || revealed.has(index)) return;

        const next = chooseOption(question, answers[question.id], optionId);
        if (next === null) return;
        answers[question.id] = next;
        committed.delete(index);
        error = null;

        // A single choice answer is locked the moment it is revealed: showing the reader the right
        // answer and then letting them change theirs would make the score meaningless.
        if (reveals && question.type === 'single_choice' && isScorable(question)) {
          revealed.add(index);
          commit(index);
          drawQuestion();
          return;
        }
        if (behaviour.autoAdvance && question.type === 'single_choice') {
          advance();
          return;
        }
        drawQuestion();
      }),
      setText: guard((value: string): void => {
        const question = config.questions[index];
        if (question === undefined) return;
        answers[question.id] = { kind: 'text', value };
        committed.delete(index);
        error = null;
        // No redraw: the field already holds what was typed, and rerendering on every keystroke is
        // how a caret ends up back at the start of the line.
      }),
      setNumber: guard((value: number): void => {
        const question = config.questions[index];
        if (question === undefined || !Number.isFinite(value)) return;
        answers[question.id] = { kind: 'number', value };
        committed.delete(index);
        error = null;
        drawQuestion();
      }),
      next: guard((): void => {
        const question = config.questions[index];
        if (question === undefined) return;
        if (!canLeave(question, answers[question.id])) {
          error = NEEDS_ANSWER;
          drawQuestion();
          return;
        }
        error = null;
        const answer = answers[question.id];
        if (reveals && isScorable(question) && !revealed.has(index) && hasAnswer(answer)) {
          revealed.add(index);
          commit(index);
          drawQuestion();
          return;
        }
        advance();
      }),
      back: guard((): void => {
        if (index === 0) return;
        index -= 1;
        error = null;
        questionShownAt = Date.now();
        drawQuestion(true);
      }),
      retry: (): void => {
        try {
          if (busy) return;
          void finish(false);
        } catch (err) {
          warnOnce('could not retry the submission', err);
        }
      },
      setEmail: signupGuard((value: string): void => {
        email = value;
        // No redraw, for the same reason as `setText`: rerendering on every keystroke is how a
        // caret ends up back at the start of the line.
      }),
      toggleConsent: signupGuard((): void => {
        ticked = !ticked;
        signupError = null;
        redraw();
      }),
      subscribe: signupGuard((): void => {
        // Inert rather than disabled: the button keeps its place in the tab order and the line
        // under it says what it is waiting for, which a dead control cannot.
        if (!ticked || subscribeUrl === null || signup === undefined || signupDone !== null) return;
        if (!looksLikeEmail(email)) {
          signupError = NEEDS_EMAIL;
          redraw();
          return;
        }
        void subscribe(subscribeUrl, consentTextOf(signup.consent));
      }),
    };

    const previousVote = behaviour.remember
      ? readVote(interactionId, consent.storage_allowed)
      : null;
    if (previousVote !== null) {
      finished = true;
      await showAggregate(previousVote, false);
    } else {
      drawQuestion();
    }
  } catch (error) {
    hide();
    warnOnce('the embed failed to start', error);
  }
}

/** Where this bundle is served from, which is where its config lives too. */
export function resolveCdnBase(script: HTMLScriptElement | null, pageUrl: string): string {
  const override = script?.getAttribute('data-cdn-base');
  if (override !== null && override !== undefined && override.length > 0) {
    return override.endsWith('/') ? override.slice(0, -1) : override;
  }
  return cdnBaseFromSrc(script?.src ?? '', pageUrl);
}

export type { LiveConfig };
